import { ph } from '@lingui/core/macro';
import { Select, Trans, useLingui } from '@lingui/react/macro';
import { cx } from 'class-variance-authority';

import type { ProviderType } from 'src/api/endpoints/cloudApi.types';
import {
  useIntegrationProviders,
  useIntegrationTermsOfService,
  useIntegrations,
} from 'src/api/queries';
import {
  NotificationBox,
  Sheet,
  SomethingWentWrong,
  Spinner,
} from 'src/components';

import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@remarkable/ark-web';
import { type ReactNode } from 'react';
import { ProviderItem } from 'src/apps/integrations/app/components/ProviderItem';
import {
  integrationLimits,
  isMaxIntegrations,
} from '../utils/isMaxIntegrations';

interface ProviderListProps {
  providerType: ProviderType;
  title?: string | ReactNode;
  description?: string | ReactNode;
  tag?: ReactNode;
  className?: string;
}

export const ProviderList = ({
  providerType,
  title,
  description,
  tag,
  className,
}: ProviderListProps) => {
  const { t } = useLingui();
  const integrations = useIntegrations(providerType);
  const providers = useIntegrationProviders();
  const tos = useIntegrationTermsOfService();
  const { user } = useAuth0();

  if (integrations.isPending || providers.isPending) return <Spinner />;
  if (integrations.isError || providers.isError) {
    return <SomethingWentWrong error={integrations.error || providers.error} />;
  }

  const filteredProviders = providerType
    ? providers.data?.filter((p) => p.providerType === providerType)
    : providers.data;

  const integrationLimit = integrationLimits[providerType];
  const atMaxIntegrations = isMaxIntegrations({
    providerType,
    integrations: integrations.data,
    user,
  });

  return (
    <Sheet
      className={cx(
        className,
        'flex flex-col gap-24',
        tos.hasAccepted ? '' : 'cursor-not-allowed opacity-70'
      )}
      data-cy={`provider-list-${providerType.toLowerCase()}`}
    >
      {title && (
        <div className="flex flex-wrap items-center gap-x-24 gap-y-16">
          <Typography fontWeight="medium" variant="interface-sm" uppercase>
            {title}
          </Typography>
          {tag}
        </div>
      )}
      {description && (
        <Typography fontWeight="regular" variant="body-md" textWrap="pretty">
          {description}
        </Typography>
      )}
      <div className="flex flex-col gap-32">
        {filteredProviders?.map((provider) => (
          <ProviderItem
            key={provider.displayName}
            provider={provider}
            disabled={atMaxIntegrations || !tos.hasAccepted}
          />
        ))}
      </div>
      {atMaxIntegrations && (
        <NotificationBox
          className="mx-0"
          variant="info"
          title={t`Maximum number of integrations reached`}
          data-cy="max-integrations-reached"
        >
          <Trans>
            At the moment we only allow {integrationLimit}{' '}
            <Select
              value={ph({ providerType: providerType.toLowerCase() })}
              _messaging="messaging integrations"
              _storage="storage integrations"
              _calendar="calendar integrations"
              other="integrations"
            />{' '}
            at a time. If you want to integrate a new service, please remove one
            of your current integrations.
          </Trans>
        </NotificationBox>
      )}
    </Sheet>
  );
};
